import { FC, ReactNode, useState } from "react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Badge from "@mui/material/Badge";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Fab from "@mui/material/Fab";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import NotificationsIcon from "@mui/icons-material/Notifications";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useUserStore } from "../../stores/user.store";
import { useActivitySidebar } from "../../hooks/useActivitySidebar";
import TodaysActivitiesList from "./TodaysActivitiesList";
import DelayedMissedList from "./DelayedMissedList";

const DRAWER_WIDTH = 360;

interface SidebarSectionProps {
	title: string;
	count: number;
	children?: ReactNode;
}

// SIMS-4146: a section header with a count badge. Renders the passed item list
// (Today's Activities — SIMS-4162; Delayed & Missed — SIMS-4178), or a simple
// count placeholder when no list is provided.
const SidebarSection: FC<SidebarSectionProps> = ({
	title,
	count,
	children,
}) => (
	<Box sx={{ px: 2, py: 1.5 }}>
		<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
			<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
				{title}
			</Typography>
			<Badge badgeContent={count} color="primary" showZero />
		</Box>
		{children ?? (
			// TODO(SIMS-4162/SIMS-4178): replace this count placeholder with the
			// real Today's Activities / Delayed & Missed item lists.
			<Typography variant="body2" color="text.secondary">
				{count === 0 ? "Nothing to show" : `${count} item(s)`}
			</Typography>
		)}
	</Box>
);

// SIMS-4146: collapsible Activity Sidebar (desktop/tablet only). Visible only
// when the coordinator is assigned to at least one Daily Workflow-enabled
// program. Two sections; "Input Submitted" (SIMS-4170) is descoped.
export const ActivitySidebar: FC = () => {
	const theme = useTheme();
	const isDesktopOrTablet = useMediaQuery(theme.breakpoints.up("sm"));
	const coordinatorId = useUserStore((state) => state.userInfo?.id);
	const { data } = useActivitySidebar(coordinatorId);
	const [open, setOpen] = useState(false);

	const todaysActivities = data?.sections.todaysActivities.count ?? 0;
	const delayedMissed = data?.sections.delayedMissed.count ?? 0;
	const total = todaysActivities + delayedMissed;

	if (!isDesktopOrTablet || !data?.programsEnabled) {
		return null;
	}

	return (
		<>
			{!open && (
				<Fab
					color="primary"
					size="medium"
					onClick={() => setOpen(true)}
					aria-label="Open activity sidebar"
					sx={{
						position: "fixed",
						right: 16,
						bottom: 24,
						zIndex: (t) => t.zIndex.drawer + 1,
					}}
				>
					<Badge badgeContent={total} color="error">
						<NotificationsIcon />
					</Badge>
				</Fab>
			)}
			<Drawer
				anchor="right"
				open={open}
				onClose={() => setOpen(false)}
				aria-labelledby="activity-sidebar-title"
				PaperProps={{ sx: { width: DRAWER_WIDTH } }}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						px: 2,
						py: 1.5,
					}}
				>
					<Typography id="activity-sidebar-title" variant="h6">
						Activity Sidebar
					</Typography>
					<IconButton
						onClick={() => setOpen(false)}
						aria-label="Collapse activity sidebar"
					>
						<ChevronRightIcon />
					</IconButton>
				</Box>
				<Divider />
				<SidebarSection title="Today's Activities" count={todaysActivities}>
					<TodaysActivitiesList coordinatorId={coordinatorId} />
				</SidebarSection>
				<Divider />
				<SidebarSection
					title="Delayed & Missed Activities"
					count={delayedMissed}
				>
					<DelayedMissedList coordinatorId={coordinatorId} />
				</SidebarSection>
			</Drawer>
		</>
	);
};

export default ActivitySidebar;
